import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Copy, Pencil, Plus, Trash2, Users, Building2 } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const TYPES = { daily: "Daily", weekly: "Weekly", end_of_day: "End of day", adhoc: "Ad hoc" };
const errMsg = (e, fb) => { const d = e?.response?.data?.detail; return Array.isArray(d) ? d[0]?.msg : d || fb; };

function AssignmentPicker({ value, onChange, people }) {
  const ids = new Set(value.user_ids);
  const toggle = (id) => { const n = new Set(ids); n.has(id) ? n.delete(id) : n.add(id); onChange({ scope: "users", user_ids: [...n] }); };
  return (
    <div className="space-y-2" data-testid="tpl-assignment">
      <div className="flex gap-2">
        {[["company", "Whole company", Building2], ["users", "Specific people", Users]].map(([k, l, I]) => (
          <button key={k} type="button" onClick={() => onChange({ scope: k, user_ids: k === "company" ? [] : value.user_ids })} data-testid={`tpl-assign-${k}`}
            className={`flex-1 h-11 rounded-md border text-sm font-medium inline-flex items-center justify-center gap-2 transition-colors ${value.scope === k ? "border-[#FF5F15] bg-orange-50 text-[#142635] ring-2 ring-[#FF5F15]/40 dark:bg-[#FF5F15] dark:text-black" : "border-border text-muted-foreground hover:bg-muted"}`}>
            <I className="w-4 h-4" />{l}
          </button>
        ))}
      </div>
      {value.scope === "users" && (
        <div className="max-h-48 overflow-y-auto rounded-md border border-border divide-y divide-border" data-testid="tpl-people-list">
          {people.map((p) => (
            <label key={p.id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-muted">
              <input type="checkbox" checked={ids.has(p.id)} onChange={() => toggle(p.id)} className="accent-[#FF5F15]" data-testid={`tpl-person-${p.id}`} />
              <span className="flex-1">{p.name}</span><span className="text-xs text-muted-foreground capitalize">{String(p.role || "").replace(/_/g, " ")}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function TemplateEditor({ tpl, people, onSaved, onCancel }) {
  const [f, setF] = useState(() => ({ name: tpl?.name || "", inspection_type: tpl?.inspection_type || "adhoc", photos_required: !!tpl?.photos_required,
    checklist: tpl?.checklist?.length ? [...tpl.checklist] : [""], assignment: tpl?.assignment || { scope: "company", user_ids: [] } }));
  const [busy, setBusy] = useState(false);
  const setItem = (i, v) => setF((s) => ({ ...s, checklist: s.checklist.map((x, j) => (j === i ? v : x)) }));
  const move = (i, d) => setF((s) => { const c = [...s.checklist]; const j = i + d; if (j < 0 || j >= c.length) return s; [c[i], c[j]] = [c[j], c[i]]; return { ...s, checklist: c }; });
  const save = async () => {
    setBusy(true);
    try {
      const body = { ...f, checklist: f.checklist.filter((x) => x.trim()) };
      const { data } = tpl ? await api.patch(`/inspections/templates/${tpl.id}`, body) : await api.post("/inspections/templates", body);
      toast.success("Inspection template saved");
      onSaved(data);
    } catch (e) { toast.error(errMsg(e, "Could not save template")); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="tpl-editor">
      <label className="block text-sm font-medium">Name<Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1 h-11" data-testid="tpl-name-input" /></label>
      <div className="grid grid-cols-2 gap-3">
        <label className="block text-sm font-medium">Type
          <select value={f.inspection_type} onChange={(e) => setF({ ...f, inspection_type: e.target.value })} className="mt-1 h-11 w-full rounded-md border border-border bg-background px-3 text-sm" data-testid="tpl-type-select">
            {Object.entries(TYPES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select></label>
        <label className="flex items-center gap-2 text-sm font-medium mt-6"><input type="checkbox" checked={f.photos_required} onChange={(e) => setF({ ...f, photos_required: e.target.checked })} className="accent-[#FF5F15]" data-testid="tpl-photos-required" />Photos required</label>
      </div>
      <div>
        <p className="text-sm font-medium mb-2">Checklist items</p>
        <div className="space-y-2">
          {f.checklist.map((item, i) => (
            <div key={i} className="flex gap-1.5">
              <Input value={item} onChange={(e) => setItem(i, e.target.value)} placeholder={`Item ${i + 1}`} maxLength={300} className="h-10" data-testid={`tpl-item-${i}`} />
              <Button type="button" size="icon" variant="ghost" onClick={() => move(i, -1)} aria-label="Move up" data-testid={`tpl-item-up-${i}`}><ArrowUp className="w-4 h-4" /></Button>
              <Button type="button" size="icon" variant="ghost" onClick={() => move(i, 1)} aria-label="Move down" data-testid={`tpl-item-down-${i}`}><ArrowDown className="w-4 h-4" /></Button>
              <Button type="button" size="icon" variant="ghost" onClick={() => setF((s) => ({ ...s, checklist: s.checklist.filter((_, j) => j !== i) }))} aria-label="Remove item" data-testid={`tpl-item-remove-${i}`}><Trash2 className="w-4 h-4" /></Button>
            </div>
          ))}
        </div>
        <Button type="button" variant="outline" size="sm" className="mt-2" onClick={() => setF((s) => ({ ...s, checklist: [...s.checklist, ""] }))} data-testid="tpl-item-add"><Plus className="w-4 h-4 mr-1" />Add item</Button>
      </div>
      <div><p className="text-sm font-medium mb-2">Who can use this inspection?</p><AssignmentPicker value={f.assignment} onChange={(a) => setF({ ...f, assignment: a })} people={people} /></div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel} data-testid="tpl-cancel">Back</Button>
        <Button onClick={save} disabled={busy || !f.name.trim()} className="bg-[#FF5F15] hover:bg-[#E65100] text-white" data-testid="tpl-save">{busy ? "Saving…" : "Save template"}</Button>
      </div>
    </div>
  );
}

export function InspectionTemplateManager({ open, onOpenChange }) {
  const [rows, setRows] = useState([]);
  const [people, setPeople] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | template
  const load = () => api.get("/inspections/templates", { __flSkipReadCache: true }).then((r) => setRows(Array.isArray(r.data) ? r.data : []));
  useEffect(() => {
    if (!open) return;
    load();
    api.get("/users").then((r) => setPeople((Array.isArray(r.data) ? r.data : r.data?.items || []).filter((u) => u.active !== false && u.role !== "super_admin"))).catch(() => {});
  }, [open]);
  const names = Object.fromEntries(people.map((p) => [p.id, p.name]));
  const clone = async (t) => { try { await api.post(`/inspections/templates/${t.id}/clone`); toast.success("Copy created"); load(); } catch (e) { toast.error(errMsg(e, "Could not copy")); } };
  const archive = async (t) => {
    if (!window.confirm(`Remove "${t.name}"? Past inspections using it are kept.`)) return;
    try { await api.delete(`/inspections/templates/${t.id}`); load(); } catch (e) { toast.error(errMsg(e, "Could not remove")); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { setEditing(null); onOpenChange(o); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="tpl-manager">
        <DialogHeader>
          <DialogTitle>{editing ? (editing === "new" ? "New inspection template" : `Edit ${editing.name}`) : "Inspection templates"}</DialogTitle>
          <DialogDescription>Customise the checklists your crews use, and choose who can use each one. Changes apply to new inspections; finished ones keep their original checklist.</DialogDescription>
        </DialogHeader>
        {editing ? (
          <TemplateEditor tpl={editing === "new" ? null : editing} people={people} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
        ) : (
          <div className="space-y-2">
            <Button onClick={() => setEditing("new")} className="bg-[#FF5F15] hover:bg-[#E65100] text-white" data-testid="tpl-new"><Plus className="w-4 h-4 mr-1.5" />New template</Button>
            {rows.map((t) => (
              <div key={t.id} className="flex items-center gap-3 rounded-md border border-border p-3" data-testid={`tpl-row-${t.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-sm truncate">{t.name}</p>
                  <p className="text-xs text-muted-foreground">{TYPES[t.inspection_type] || t.inspection_type} · {t.checklist?.length || 0} items · {t.assignment?.scope === "users" ? `Assigned to ${t.assignment.user_ids.map((id) => names[id]).filter(Boolean).join(", ") || "people no longer in your company"}` : "Whole company"}</p>
                </div>
                <Button size="icon" variant="ghost" onClick={() => setEditing(t)} aria-label="Edit" data-testid={`tpl-edit-${t.id}`}><Pencil className="w-4 h-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => clone(t)} aria-label="Copy" data-testid={`tpl-clone-${t.id}`}><Copy className="w-4 h-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => archive(t)} aria-label="Remove" data-testid={`tpl-remove-${t.id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
